import { table, restApiKey } from '../../../../_constants/Code'
import { PathInfoList } from '../../../../_constants/PathInfoList'
import { getDynamoDB } from '../../../../common/utils/DynamoDbUtils'
import { httpGet, httpPost } from '../../../../services/api/AmplifyApi'
import { toApiDate } from './helpers'

/**
 * 期間一覧を取得（DepartmentInfo テーブルから。batch未実装のため暫定）
 *
 * @param {string} corporationNumber - 法人番号
 * @returns {Promise<Object[]>} DepartmentInfo のレコード配列（有効開始日降順想定でソートは呼び出し側で実施可）
 */
export async function fetchPeriodList(corporationNumber) {
  const res = await getDynamoDB(table.DepartmentInfo, null, corporationNumber)
  const data = res?.body ?? res
  return Array.isArray(data) ? data : []
}

/**
 * 組織階層マスター管理テーブル（DepartmentHierarchy）から DepartmentHierarchyCount を期間別に取得
 *
 * @param {string} corporationNumber - 法人番号
 * @returns {Promise<Record<string, number>>} 期間開始日(YYYY/MM/DD) -> 階層数 のマップ
 */
export async function fetchHierarchyCountByPeriod(corporationNumber) {
  const res = await getDynamoDB(table.DepartmentHierarchy, null, corporationNumber)
  const data = res?.body ?? res
  const list = Array.isArray(data) ? data : []
  const map = {}
  list.forEach((rec) => {
    const startDate = toApiDate(
      rec.StartDate ??
        rec.start_date ??
        rec.startDate ??
        (typeof rec.SearchKey === 'string' && rec.SearchKey.includes('#') ? rec.SearchKey.split('#')[0] : '')
    )
    if (startDate) {
      map[startDate] = rec.DepartmentHierarchyCount ?? 6
    }
  })
  return map
}

/**
 * 組織情報設定取得（IF02074）
 *
 * @param {string} corporationNumber - 法人番号
 * @param {{ start_date: string; end_date?: string }} targetPeriod - { start_date: 'YYYY/MM/DD', end_date: 'YYYY/MM/DD'
 *   | '' }
 * @returns {Promise<Object>} レスポンス（body.target_period.department_info, segment_list 等）
 */
export async function getDepartmentInfo(corporationNumber, targetPeriod) {
  const startDate = toApiDate(targetPeriod?.start_date ?? '') || ''
  const endDate =
    targetPeriod?.end_date != null && targetPeriod?.end_date !== '' ? toApiDate(targetPeriod.end_date) : ''
  if (!startDate) {
    throw new Error('対象期間の開始日（YYYY/MM/DD）を指定してください')
  }
  const queryParams = {
    start_date: startDate,
    end_date: endDate === undefined || endDate === null ? '' : String(endDate)
  }
  const init = {
    headers: {},
    queryStringParameters: queryParams
  }
  const path = PathInfoList.Resource.getDepartmentInfo(corporationNumber)
  const res = await httpGet(restApiKey.CHROFY_REST_API, path, init)
  return res
}

/**
 * 組織階層マスター取得（IF02075） バックエンドは GET + queryStringParameters（start_date）で定義されているため、POST ではなく GET で呼ぶ。
 *
 * @param {string} corporationNumber - 法人番号
 * @param {{ start_date: string }} targetPeriod - { start_date: 'YYYY/MM/DD' }
 * @returns {Promise<Object>} レスポンス（body.target_period.master 等。drilldown の processed は返さない）
 */
export async function getDepartmentHierarchy(corporationNumber, targetPeriod) {
  const startDate = toApiDate(targetPeriod?.start_date ?? '') || ''
  if (!startDate) {
    throw new Error('対象期間の開始日（YYYY/MM/DD）を指定してください')
  }
  const init = {
    headers: {},
    queryStringParameters: { start_date: startDate }
  }
  const res = await httpGet(
    restApiKey.CHROFY_REST_API,
    PathInfoList.Resource.getDepartmentHierarchy(corporationNumber),
    init
  )
  return res
}

/**
 * 組織情報設定更新（IF02072）
 *
 * @param {string} corporationNumber - 法人番号
 * @param {{
 *   table_data: Object[]
 *   hierarchy_data: (string | number)[][]
 *   target_period: { start_date: string; end_date: string | null }
 * }} payload
 *   - 更新データ
 *
 * @returns {Promise<Object>} API レスポンス
 */
export async function updateDepartmentInfo(corporationNumber, payload) {
  const init = {
    headers: {},
    body: payload
  }
  return httpPost(restApiKey.CHROFY_REST_API, PathInfoList.Resource.updateDepartmentInfo(corporationNumber), init)
}

/**
 * 組織情報・組織階層設定削除（IF02073）
 *
 * @param {string} corporationNumber - 法人番号
 * @param {{ start_date: string }} targetPeriod - { start_date: 'YYYY/MM/DD' }
 * @returns {Promise<Object>} API レスポンス
 */
export async function deleteDepartmentData(corporationNumber, targetPeriod) {
  const init = {
    headers: {},
    body: { target_period: targetPeriod }
  }
  return httpPost(restApiKey.CHROFY_REST_API, PathInfoList.Resource.deleteDepartmentData(corporationNumber), init)
}
